import { useState, useContext, useEffect, useCallback } from "react";
import '../../styles/page-titles.css'
import PermissionGate from "../../services/PermissionGate";
import styled from 'styled-components';
import { SidebarContext } from "../../AppRoutes";
import { getRelatorioHorarios } from "../../services/api";
import { nowTz } from '../../utils/timezone';
import Loading from "react-loading";
import { FaClock, FaCalendarAlt, FaChartBar, FaSun, FaMoon, FaCloudSun } from 'react-icons/fa';
import { MdAccessTime, MdTrendingUp, MdWbSunny } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatPriceWithSymbol } from '../../utils/currency';
import regionConfig from '../../config/regionConfig';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    LineChart, Line, Legend, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
    Cell
} from 'recharts';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    transition: margin-left 200ms ease;
    background-color: #ffffff;
    padding: 24px;

    @media (max-width: 880px) {
        margin-left: 0;
        padding: 16px;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;

    .filters {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
    }

    input[type="date"] {
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 14px;
    }

    button {
        padding: 8px 16px;
        background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 500;
        transition: opacity 0.2s;

        &:hover {
            opacity: 0.9;
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }

    @media (max-width: 600px) {
        h1 {
            font-size: 18px;
        }

        .filters {
            width: 100%;
        }

        input[type="date"], button {
            flex: 1;
        }
    }
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 500px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
`;

const MetricCard = styled.div`
    background: white;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    gap: 8px;

    .icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        margin-bottom: 8px;
    }

    .icon.blue { background: rgba(49, 140, 213, 0.1); color: rgb(49, 140, 213); }
    .icon.orange { background: rgba(255, 152, 0, 0.1); color: #ff9800; }
    .icon.green { background: rgba(40, 167, 69, 0.1); color: #28a745; }
    .icon.purple { background: rgba(102, 126, 234, 0.1); color: #667eea; }

    .label {
        font-size: 14px;
        color: #666;
    }

    .value {
        font-size: 28px;
        font-weight: 700;
        color: #333;
    }

    .sub {
        font-size: 12px;
        color: #999;
    }

    @media (max-width: 500px) {
        padding: 14px;
        .value { font-size: 22px; }
    }
`;

const ChartsRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 1000px) {
        grid-template-columns: 1fr;
    }
`;

const ChartCard = styled.div`
    background: white;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .chart-container {
        height: 300px;
        width: 100%;
    }

    @media (max-width: 600px) {
        padding: 16px;

        .chart-container {
            height: 250px;
        }
    }
`;

const FullWidthChart = styled(ChartCard)`
    grid-column: 1 / -1;

    .chart-container {
        height: 400px;
    }
`;

const HeatmapCard = styled.div`
    background: white;
    padding: 24px;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    margin-bottom: 30px;
    overflow-x: auto;

    h3 {
        font-size: 18px;
        font-weight: 600;
        color: #333;
        margin-bottom: 24px;
        display: flex;
        align-items: center;
        gap: 10px;
    }
`;

const Heatmap = styled.div`
    display: grid;
    grid-template-columns: 80px repeat(24, 1fr);
    gap: 2px;
    min-width: 800px;

    .day-label {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        padding-right: 12px;
        font-weight: 600;
        color: #666;
        font-size: 13px;
    }

    .hour-labels {
        display: contents;

        .hour-label {
            text-align: center;
            font-size: 11px;
            color: #999;
            padding-bottom: 8px;
        }

        .empty {
            /* Empty cell for alignment */
        }
    }

    .heat-cell {
        aspect-ratio: 1;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 10px;
        font-weight: 600;
        color: white;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        cursor: pointer;
        min-width: 28px;
        min-height: 28px;

        &:hover {
            transform: scale(1.2);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
            z-index: 10;
        }
    }
`;

const HeatmapLegend = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 20px;

    .legend-label {
        font-size: 12px;
        color: #666;
    }

    .legend-gradient {
        display: flex;
        height: 16px;
        border-radius: 4px;
        overflow: hidden;

        .segment {
            width: 30px;
        }
    }
`;

const PeriodCards = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 30px;
`;

const PeriodCard = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    padding: 24px;
    text-align: center;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: ${props => props.color || 'rgb(49, 140, 213)'};
    }

    .icon-container {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 28px;
        margin: 0 auto 16px;
        background: ${props => props.bgColor || 'rgba(49, 140, 213, 0.1)'};
        color: ${props => props.color || 'rgb(49, 140, 213)'};
    }

    .period-name {
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin-bottom: 4px;
    }

    .period-hours {
        font-size: 12px;
        color: #999;
        margin-bottom: 16px;
    }

    .stats-row {
        display: flex;
        justify-content: space-around;

        .stat {
            .value {
                font-size: 20px;
                font-weight: 700;
                color: #333;
            }

            .label {
                font-size: 11px;
                color: #666;
            }
        }
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
`;

const getHeatColor = (value, max) => {
    if (value === 0) return '#f0f0f0';
    const intensity = value / max;

    if (intensity < 0.2) return '#d4edda';
    if (intensity < 0.4) return '#a8d5ba';
    if (intensity < 0.6) return '#74c69d';
    if (intensity < 0.8) return '#40916c';
    return '#1b4332';
};

const PERIOD_CONFIG = {
    manha: {
        icon: FaSun,
        color: '#ff9800',
        bgColor: 'rgba(255, 152, 0, 0.1)',
    },
    tarde: {
        icon: FaCloudSun,
        color: '#e17055',
        bgColor: 'rgba(225, 112, 85, 0.1)',
    },
    noite: {
        icon: FaMoon,
        color: '#667eea',
        bgColor: 'rgba(102, 126, 234, 0.1)',
    },
    madrugada: {
        icon: MdWbSunny,
        color: '#2d3436',
        bgColor: 'rgba(45, 52, 54, 0.1)',
    }
};

const RelatorioHorarios = () => {
    const empresa = localStorage.getItem('empresa');
    const empresaParse = JSON.parse(empresa);

    const { sidebar } = useContext(SidebarContext);
    const { t } = useTranslation('reports');

    const DAYS_OF_WEEK = [
        t('peakHours.daysOfWeek.sunday'), t('peakHours.daysOfWeek.monday'),
        t('peakHours.daysOfWeek.tuesday'), t('peakHours.daysOfWeek.wednesday'),
        t('peakHours.daysOfWeek.thursday'), t('peakHours.daysOfWeek.friday'),
        t('peakHours.daysOfWeek.saturday')
    ];
    const DAYS_SHORT = [
        t('peakHours.daysShort.sunday'), t('peakHours.daysShort.monday'),
        t('peakHours.daysShort.tuesday'), t('peakHours.daysShort.wednesday'),
        t('peakHours.daysShort.thursday'), t('peakHours.daysShort.friday'),
        t('peakHours.daysShort.saturday')
    ];

    // O backend devolve o nome do dia em pt-BR (ex.: "Sábado"). Mapeamos pelo índice
    // para exibir sempre no idioma corrente.
    const BACKEND_DAY_NAMES = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    const translateDayName = (name) => {
        if (!name) return name;
        const index = BACKEND_DAY_NAMES.indexOf(name);
        return index >= 0 ? DAYS_OF_WEEK[index] : name;
    };

    const getPeriodLabel = (key) => {
        const labels = {
            manha: t('peakHours.periods.morning'),
            tarde: t('peakHours.periods.afternoon'),
            noite: t('peakHours.periods.evening'),
            madrugada: t('peakHours.periods.lateNight')
        };
        return labels[key] || key;
    };

    const getPeriodHours = (key) => {
        const hours = {
            manha: t('peakHours.periodHours.morning'),
            tarde: t('peakHours.periodHours.afternoon'),
            noite: t('peakHours.periodHours.evening'),
            madrugada: t('peakHours.periodHours.lateNight')
        };
        return hours[key] || '';
    };

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState({
        resumo: {
            hora_pico: '19:00',
            dia_pico: '',
            total_pedidos: 0,
            pedidos_hora_pico: 0,
            pedidos_dia_pico: 0,
            horarios_quentes: []
        },
        por_hora: [],
        por_dia_semana: [],
        por_faixa: [],
        insights: {}
    });
    const [startDate, setStartDate] = useState(nowTz().subtract(30, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(nowTz().format('YYYY-MM-DD'));

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getRelatorioHorarios(startDate, endDate);
            if (response?.data?.success) {
                setData(response.data);
            }
        } catch (error) {
            console.error('[RELATORIO-HORARIOS] Erro ao buscar dados:', error);
            toast.error(t('common.fetchError'));
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Preparar dados para gráfico de barras por hora
    const horasData = (data.por_hora || []).map(h => ({
        hora: h.label || `${h.hora}h`,
        pedidos: h.pedidos || 0,
        faturamento: h.faturamento || 0
    }));

    // Preparar dados para gráfico radar por dia da semana
    const diasData = (data.por_dia_semana || []).map(d => ({
        dia: DAYS_SHORT[d.dia_numero] || translateDayName(d.dia) || t('peakHours.dayNumber', { number: d.dia_numero }),
        pedidos: d.pedidos || 0,
        faturamento: d.faturamento || 0
    }));

    // Calcular média de pedidos por hora
    const totalPedidos = data.resumo?.total_pedidos || 0;
    const mediaPorHora = horasData.length > 0 ? totalPedidos / horasData.length : 0;

    // Gerar heatmap a partir de por_hora e por_dia_semana
    const heatmapData = {};
    let maxValue = 1;

    // Para cada combinação de dia e hora, calcular estimativa de pedidos
    // Como o backend não retorna heatmap direto, vamos criar aproximação
    (data.por_hora || []).forEach(hora => {
        (data.por_dia_semana || []).forEach(dia => {
            const key = `${dia.dia_numero}-${hora.hora}`;
            // Distribuição proporcional baseada nos totais
            const diaTotal = dia.pedidos || 0;
            const horaTotal = hora.pedidos || 0;
            const estimate = totalPedidos > 0
                ? Math.round((diaTotal * horaTotal) / totalPedidos)
                : 0;
            heatmapData[key] = estimate;
            if (estimate > maxValue) maxValue = estimate;
        });
    });

    const formatCurrency = (value) => {
        return formatPriceWithSymbol(value || 0);
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            // Verifica se o campo deve ser formatado como moeda
            const isCurrencyField = (name, dataKey) => {
                const currencyKeywords = ['faturamento', 'valor', 'value', 'total', 'ticket', 'preco', 'receita'];
                const lowerName = (name || '').toLowerCase();
                const lowerKey = (dataKey || '').toLowerCase();
                return currencyKeywords.some(kw => lowerName.includes(kw) || lowerKey.includes(kw));
            };

            return (
                <div style={{
                    background: 'white',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: '1px solid #e0e0e0'
                }}>
                    <p style={{ margin: 0, fontWeight: 600, color: '#333' }}>{label || payload[0]?.payload?.name}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ margin: '4px 0 0', color: entry.color || '#318CD5', fontSize: '13px' }}>
                            {entry.name}: {isCurrencyField(entry.name, entry.dataKey)
                                ? formatCurrency(entry.value)
                                : entry.value?.toLocaleString?.(regionConfig.currency.locale) || entry.value}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <>
            <PermissionGate permissions={['default']}>
                <Container sidebar={sidebar}>
                    <Header>
                        <div className="page-title-with-icon">
                            <div className="page-icon"><FaClock /></div>
                            <h1 className="page-title">{t('peakHours.title')}</h1>
                        </div>
                        <div className="filters" style={{ marginRight: '50px' }}>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                            <button onClick={fetchData} disabled={isLoading}>
                                {isLoading ? t('common.loading') : t('common.update')}
                            </button>
                        </div>
                    </Header>

                    {isLoading ? (
                        <LoadingContainer>
                            <Loading type="spin" color="rgb(49, 140, 213)" height={40} width={40} />
                        </LoadingContainer>
                    ) : (
                        <>
                            <CardsGrid>
                                <MetricCard>
                                    <div className="icon orange">
                                        <MdAccessTime />
                                    </div>
                                    <span className="label">{t('peakHours.peakTime')}</span>
                                    <span className="value">{data.resumo?.hora_pico || '19:00'}</span>
                                    <span className="sub">{t('peakHours.peakTimeSub')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon purple">
                                        <FaCalendarAlt />
                                    </div>
                                    <span className="label">{t('peakHours.busiestDay')}</span>
                                    <span className="value">{translateDayName(data.resumo?.dia_pico) || DAYS_OF_WEEK[6]}</span>
                                    <span className="sub">{t('peakHours.busiestDaySub')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon blue">
                                        <FaChartBar />
                                    </div>
                                    <span className="label">{t('peakHours.avgPerHour')}</span>
                                    <span className="value">{mediaPorHora.toFixed(1)}</span>
                                    <span className="sub">{t('peakHours.avgPerHourSub')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon green">
                                        <MdTrendingUp />
                                    </div>
                                    <span className="label">{t('peakHours.totalInPeriod')}</span>
                                    <span className="value">{data.resumo?.total_pedidos || 0}</span>
                                    <span className="sub">{t('peakHours.totalInPeriodSub')}</span>
                                </MetricCard>
                            </CardsGrid>

                            <PeriodCards>
                                {(data.por_faixa || []).slice(0, 4).map((faixa, index) => {
                                    // Map faixa labels to config
                                    const configMap = {
                                        '06:00-12:00': 'manha',
                                        '12:00-14:00': 'almoco',
                                        '14:00-18:00': 'tarde',
                                        '18:00-21:00': 'jantar',
                                        '21:00-00:00': 'noite',
                                        '00:00-06:00': 'madrugada'
                                    };
                                    const periodKey = configMap[faixa.label] || 'manha';
                                    const config = PERIOD_CONFIG[periodKey] || PERIOD_CONFIG.manha;
                                    const Icon = config.icon;

                                    return (
                                        <PeriodCard key={faixa.label || index} color={config.color} bgColor={config.bgColor}>
                                            <div className="icon-container">
                                                <Icon />
                                            </div>
                                            <div className="period-name">{faixa.label || getPeriodLabel(periodKey)}</div>
                                            <div className="period-hours">{t('common.ofTotal', { value: faixa.percentual || 0 })}</div>
                                            <div className="stats-row">
                                                <div className="stat">
                                                    <div className="value">{faixa.pedidos || 0}</div>
                                                    <div className="label">{t('peakHours.ordersLabel')}</div>
                                                </div>
                                                <div className="stat">
                                                    <div className="value">
                                                        {formatPriceWithSymbol(faixa.faturamento || 0)}
                                                    </div>
                                                    <div className="label">{t('peakHours.revenueLabel')}</div>
                                                </div>
                                            </div>
                                        </PeriodCard>
                                    );
                                })}
                            </PeriodCards>

                            <HeatmapCard>
                                <h3>
                                    <FaClock color="rgb(49, 140, 213)" />
                                    {t('peakHours.heatmapTitle')}
                                </h3>
                                <Heatmap>
                                    <div className="hour-labels">
                                        <div className="empty"></div>
                                        {Array.from({ length: 24 }, (_, i) => (
                                            <div key={i} className="hour-label">{i}h</div>
                                        ))}
                                    </div>
                                    {DAYS_OF_WEEK.map((day, dayIndex) => (
                                        <>
                                            <div key={`label-${dayIndex}`} className="day-label">{day}</div>
                                            {Array.from({ length: 24 }, (_, hour) => {
                                                const value = heatmapData[`${dayIndex}-${hour}`] || 0;
                                                return (
                                                    <div
                                                        key={`${dayIndex}-${hour}`}
                                                        className="heat-cell"
                                                        style={{ backgroundColor: getHeatColor(value, maxValue) }}
                                                        title={t('peakHours.heatTooltip', { day, hour, value })}
                                                    >
                                                        {value > 0 && value}
                                                    </div>
                                                );
                                            })}
                                        </>
                                    ))}
                                </Heatmap>
                                <HeatmapLegend>
                                    <span className="legend-label">{t('peakHours.heatLegend.less')}</span>
                                    <div className="legend-gradient">
                                        <div className="segment" style={{ background: '#f0f0f0' }}></div>
                                        <div className="segment" style={{ background: '#d4edda' }}></div>
                                        <div className="segment" style={{ background: '#a8d5ba' }}></div>
                                        <div className="segment" style={{ background: '#74c69d' }}></div>
                                        <div className="segment" style={{ background: '#40916c' }}></div>
                                        <div className="segment" style={{ background: '#1b4332' }}></div>
                                    </div>
                                    <span className="legend-label">{t('peakHours.heatLegend.more')}</span>
                                </HeatmapLegend>
                            </HeatmapCard>

                            <ChartsRow>
                                <ChartCard>
                                    <h3>
                                        <MdAccessTime />
                                        {t('peakHours.ordersByHour')}
                                    </h3>
                                    <div className="chart-container">
                                        {horasData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={horasData}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="hora" tick={{ fontSize: 10 }} />
                                                    <YAxis />
                                                    <Tooltip content={<CustomTooltip />} />
                                                    <Bar dataKey="pedidos" name={t('peakHours.ordersLabel')} fill="#318CD5" radius={[4, 4, 0, 0]}>
                                                        {horasData.map((entry, index) => (
                                                            <Cell
                                                                key={`cell-${index}`}
                                                                fill={entry.pedidos === Math.max(...horasData.map(h => h.pedidos))
                                                                    ? '#ff9800'
                                                                    : '#318CD5'
                                                                }
                                                            />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
                                                {t('common.noData')}
                                            </div>
                                        )}
                                    </div>
                                </ChartCard>

                                <ChartCard>
                                    <h3>
                                        <FaCalendarAlt />
                                        {t('peakHours.ordersByDay')}
                                    </h3>
                                    <div className="chart-container">
                                        {diasData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <RadarChart data={diasData}>
                                                    <PolarGrid />
                                                    <PolarAngleAxis dataKey="dia" />
                                                    <PolarRadiusAxis angle={30} domain={[0, 'auto']} />
                                                    <Radar
                                                        name={t('peakHours.ordersLabel')}
                                                        dataKey="pedidos"
                                                        stroke="#318CD5"
                                                        fill="#318CD5"
                                                        fillOpacity={0.5}
                                                    />
                                                    <Tooltip content={<CustomTooltip />} />
                                                    <Legend />
                                                </RadarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
                                                {t('common.noData')}
                                            </div>
                                        )}
                                    </div>
                                </ChartCard>
                            </ChartsRow>

                            <FullWidthChart>
                                <h3>
                                    <MdTrendingUp />
                                    {t('peakHours.revenueByHour')}
                                </h3>
                                <div className="chart-container">
                                    {horasData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={horasData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="hora" />
                                                <YAxis tickFormatter={(value) => formatPriceWithSymbol(value)} />
                                                <Tooltip content={<CustomTooltip />} />
                                                <Legend />
                                                <Line
                                                    type="monotone"
                                                    dataKey="faturamento"
                                                    name={t('peakHours.revenueLabel')}
                                                    stroke="#28a745"
                                                    strokeWidth={3}
                                                    dot={{ fill: '#28a745', strokeWidth: 2, r: 4 }}
                                                    activeDot={{ r: 8 }}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
                                            {t('common.noData')}
                                        </div>
                                    )}
                                </div>
                            </FullWidthChart>
                        </>
                    )}
                </Container>
            </PermissionGate>
        </>
    );
};

export default RelatorioHorarios;
